/**
 * NotificationsInboxController — section « Inbox » de /notifications.html
 * (EPIC-033 / 33.6).
 *
 * Hydrate la liste paginée des envois `outbound_email` depuis
 * GET /me/notifications. Tri DESC `created_at` côté serveur ; filtres
 * `category`, `status`, `from`, `to` envoyés en query params (camelCase).
 * Recherche locale par sujet (filtrage client-side de la page courante,
 * non envoyée à l'API en V1). Persistance des filtres en sessionStorage
 * (clé `STORAGE_KEY`) pour résilience au rechargement de page.
 *
 * Au clic sur un item, dispatch `notifications:inbox-item-selected` sur
 * `window` (detail `{id}`) — consommé par
 * {@see NotificationsInboxDetailController} pour charger le détail dans
 * la colonne droite (panneau « Email du moment » de la maquette).
 *
 * Convention CLAUDE.md : pas d'HTML inline en JS, les rows sont clonées
 * depuis `<template id="tpl-inbox-row">` puis remplies via
 * `cloneTemplate` / `fillSlots`.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const STORAGE_KEY = "notamicro:notifications-inbox:filters";
const DEFAULT_PER_PAGE = 25;

const CATEGORY_LABELS = {
  urssaf: "URSSAF",
  tva: "TVA",
  das2: "DAS2",
  inpi: "INPI",
  factures: "Factures",
  relances: "Relances",
  aides: "Aides",
  cfe: "CFE",
  compte: "Compte",
  cycle_vie_compte: "Cycle de vie",
  archivage_post_resiliation: "Archivage",
  sortie_franchise_tva: "Sortie TVA",
  seuils: "Seuils",
  versement_liberatoire: "Versement libératoire",
  registres_obligations: "Registres",
  wero: "Wero",
  recurrences: "Récurrent",
  documents_publics: "Documents",
};

const CATEGORY_PILL_CLASSES = {
  urssaf: "nm-pill--red",
  tva: "nm-pill--ochre",
  inpi: "nm-pill--indigo",
  das2: "nm-pill--olive",
  factures: "nm-pill--ochre",
  relances: "nm-pill--ochre",
  aides: "nm-pill--blue",
  cfe: "nm-pill--blue",
  wero: "nm-pill--olive",
  recurrences: "nm-pill--indigo",
  seuils: "nm-pill--ochre",
  compte: "nm-pill--blue",
};

const STATUS_LABELS = {
  queued: "En attente",
  sent: "Envoyé",
  delivered: "Livré",
  opened: "Ouvert",
  bounced: "Rebond",
  failed: "Échec",
};

export default class NotificationsInboxController extends BaseController {
  static targets = [
    "list",
    "count",
    "paginationLabel",
    "prevButton",
    "nextButton",
    "categorySelect",
    "statusSelect",
    "fromInput",
    "toInput",
    "searchInput",
    "empty",
  ];

  state = {
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    total: 0,
    totalPages: 1,
    filters: { category: "", status: "", from: "", to: "" },
    search: "",
    items: [],
    loading: false,
  };

  async connect() {
    this._restoreFilters();
    this._applyFiltersToInputs();
    await this._reload();
  }

  onFilterChange() {
    this._readFiltersFromInputs();
    this.state.page = 1;
    this._persistFilters();
    this._reload();
  }

  onSearch(event) {
    this.state.search = String((event?.target?.value) || "").trim().toLowerCase();
    this._renderList();
  }

  onPrev() {
    if (this.state.page <= 1) return;
    this.state.page -= 1;
    this._reload();
  }

  onNext() {
    if (this.state.page >= this.state.totalPages) return;
    this.state.page += 1;
    this._reload();
  }

  onItemClick(event) {
    const li = event.currentTarget;
    if (!(li instanceof HTMLElement)) return;
    const id = li.dataset.notificationId;
    if (!id) return;
    if (this.hasListTarget) {
      this.listTarget.querySelectorAll("li").forEach((el) => {
        el.removeAttribute("data-selected");
      });
    }
    li.setAttribute("data-selected", "true");
    window.dispatchEvent(
      new CustomEvent("notifications:inbox-item-selected", { detail: { id } }),
    );
  }

  async _reload() {
    if (this.state.loading) return;
    this.state.loading = true;
    try {
      const params = {
        page: String(this.state.page),
        perPage: String(this.state.perPage),
      };
      if (this.state.filters.category) params.category = this.state.filters.category;
      if (this.state.filters.status) params.status = this.state.filters.status;
      if (this.state.filters.from) params.from = this.state.filters.from;
      if (this.state.filters.to) params.to = this.state.filters.to;

      const data = await api.get("/me/notifications", params);
      this.state.items = Array.isArray(data?.data) ? data.data : [];
      this.state.total = Number(data?.pagination?.total ?? this.state.items.length);
      this.state.totalPages = Number(data?.pagination?.totalPages ?? 1);
      this._renderList();
      this._renderPagination();
    } catch (error) {
      console.warn("[notifications-inbox] reload failed", error);
      this.showError("Impossible de charger l'inbox.");
    } finally {
      this.state.loading = false;
    }
  }

  _renderList() {
    if (!this.hasListTarget) return;
    while (this.listTarget.firstChild) {
      this.listTarget.removeChild(this.listTarget.firstChild);
    }
    const items = this.state.search === ""
      ? this.state.items
      : this.state.items.filter((it) => {
        const subject = String(it.subjectSnapshot || "").toLowerCase();
        return subject.includes(this.state.search);
      });

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = items.length !== 0;
    }

    for (const item of items) {
      const fragment = this.cloneTemplate("tpl-inbox-row");
      if (!fragment) continue;
      this.fillSlots(fragment, {
        subject: item.subjectSnapshot || "(sans objet)",
        categoryLabel: this._categoryLabel(item.category),
        statusLabel: this._statusLabel(item.status),
        recipientEmail: item.recipientEmail || "",
        sentAtRelative: item.sentAt
          ? this.formatTimeAgo(item.sentAt)
          : "—",
      });
      const li = fragment.querySelector("li");
      if (li) {
        li.dataset.notificationId = item.id;
        li.setAttribute("data-action", "click->notifications-inbox#onItemClick");
        li.setAttribute("tabindex", "0");
        li.setAttribute("role", "button");
        li.setAttribute(
          "aria-label",
          `Email « ${item.subjectSnapshot || "(sans objet)"} », statut ${this._statusLabel(item.status)}`,
        );
      }
      const pill = fragment.querySelector(".nm-pill");
      if (pill) {
        pill.className = "nm-pill " + this._categoryPillClass(item.category);
      }
      this.listTarget.appendChild(fragment);
    }

    if (this.hasCountTarget) {
      const total = this.state.total;
      this.countTarget.textContent = `${total} notification${total > 1 ? "s" : ""}`;
    }
  }

  _renderPagination() {
    const totalPages = Math.max(1, this.state.totalPages);
    if (this.hasPaginationLabelTarget) {
      this.paginationLabelTarget.textContent = `Page ${this.state.page} sur ${totalPages} — ${this.state.items.length} sur ${this.state.total}`;
    }
    if (this.hasPrevButtonTarget) this.prevButtonTarget.disabled = this.state.page <= 1;
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = this.state.page >= totalPages;
  }

  _readFiltersFromInputs() {
    if (this.hasCategorySelectTarget) this.state.filters.category = this.categorySelectTarget.value || "";
    if (this.hasStatusSelectTarget) this.state.filters.status = this.statusSelectTarget.value || "";
    if (this.hasFromInputTarget) this.state.filters.from = this.fromInputTarget.value || "";
    if (this.hasToInputTarget) this.state.filters.to = this.toInputTarget.value || "";
  }

  _applyFiltersToInputs() {
    if (this.hasCategorySelectTarget) this.categorySelectTarget.value = this.state.filters.category;
    if (this.hasStatusSelectTarget) this.statusSelectTarget.value = this.state.filters.status;
    if (this.hasFromInputTarget) this.fromInputTarget.value = this.state.filters.from;
    if (this.hasToInputTarget) this.toInputTarget.value = this.state.filters.to;
  }

  _persistFilters() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state.filters));
    } catch {
      // sessionStorage indisponible (mode privé strict) — on ignore : la
      // résilience cross-reload est un bonus, pas un invariant.
    }
  }

  _restoreFilters() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const decoded = JSON.parse(raw);
      if (decoded && typeof decoded === "object") {
        Object.assign(this.state.filters, decoded);
      }
    } catch {
      // valeur stockée invalide — on continue avec les filtres par défaut.
    }
  }

  _categoryLabel(category) {
    return CATEGORY_LABELS[category] || category || "—";
  }

  _statusLabel(status) {
    return STATUS_LABELS[status] || status || "—";
  }

  _categoryPillClass(category) {
    return CATEGORY_PILL_CLASSES[category] || "nm-pill--blue";
  }
}
